// hl-info-api — REST-Wrapper für den Hyperliquid `POST /info`-Endpoint.
// Alle Calls schicken `{ type: '<request_type>' }` als Body + akzeptieren
// HL's Raw-JSON-Antworten. Pure Funktionen — Hooks bauen drauf auf.

const HL_INFO_URL = 'https://api.hyperliquid.xyz/info';

interface InfoRequest {
  type: string;
  [key: string]: unknown;
}

async function postInfo<T>(body: InfoRequest, timeoutMs = 5000): Promise<T | null> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(HL_INFO_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: ctrl.signal,
    });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') return null;
    console.warn(`[hl-info] ${body.type} failed:`, e instanceof Error ? e.message : e);
    return null;
  } finally {
    clearTimeout(timer);
  }
}

// ── meta + assetCtx (alle Coins) ─────────────────────────────────────────────

interface HlMetaUniverseEntry {
  name: string;
  szDecimals: number;
  maxLeverage?: number;
  /** Wenn true: Coin ist nur via WS subscription erreichbar (delisted/etc). */
  onlyIsolated?: boolean;
}

interface HlMetaResponse {
  universe: HlMetaUniverseEntry[];
}

/** AssetCtx aus dem REST-Endpoint (mehr Felder als WS-activeAssetCtx). */
export interface HlAssetCtx {
  /** Coin-Name (aus universe-Index abgeleitet, nicht im Raw-Response). */
  coin: string;
  /** Mark-Preis aktuell. */
  markPx: number;
  /** Oracle-Preis (HL-internal). */
  oraclePx: number;
  /** Premium markPx vs. oraclePx als Decimal (z.B. 0.000234 = 0.0234%). */
  premium: number;
  /** Aktuelles Funding (Decimal pro Stunde). */
  funding: number;
  /** Open Interest in Coin-Units. */
  openInterest: number;
  /** 24h Notional Volume in USD. */
  dayNtlVlm: number;
  /** Mid-Preis (von HL geliefert). */
  midPx: number | null;
  /** Vortags-Schluss. */
  prevDayPx: number;
}

type HlAssetCtxRaw = Record<string, unknown>;

const num = (v: unknown): number => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

const numOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined) return null;
  const n = num(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Lädt vollständige Metrics aller Coins. Antwort von HL:
 * `[meta, assetCtxArray]` — Array hat die gleiche Reihenfolge wie meta.universe.
 */
export async function fetchMetaAndAssetCtxs(): Promise<Map<string, HlAssetCtx>> {
  type Response = [HlMetaResponse, HlAssetCtxRaw[]];
  const result = await postInfo<Response>({ type: 'metaAndAssetCtxs' });
  if (!result) return new Map();
  const [meta, ctxs] = result;
  const map = new Map<string, HlAssetCtx>();
  meta.universe.forEach((u, i) => {
    const raw = ctxs[i];
    if (!raw) return;
    map.set(u.name, {
      coin: u.name,
      markPx: num(raw['markPx']),
      oraclePx: num(raw['oraclePx']),
      premium: num(raw['premium']),
      funding: num(raw['funding']),
      openInterest: num(raw['openInterest']),
      dayNtlVlm: num(raw['dayNtlVlm']),
      midPx: numOrNull(raw['midPx']),
      prevDayPx: num(raw['prevDayPx']),
    });
  });
  return map;
}

// ── predictedFundings (Cross-Venue Funding-Rates) ────────────────────────────

export interface PredictedFundingVenue {
  venue: string;
  fundingRate: number;
  nextFundingTime?: number;
}

export interface PredictedFundingCoin {
  coin: string;
  venues: PredictedFundingVenue[];
}

/**
 * Antwort von HL: Array `[[coin, [[venueName, {fundingRate, nextFundingTime}]...]]]`.
 */
export async function fetchPredictedFundings(): Promise<PredictedFundingCoin[]> {
  type VenueTuple = [string, { fundingRate?: string | null; nextFundingTime?: number }];
  type Raw = [string, VenueTuple[]][];
  const result = await postInfo<Raw>({ type: 'predictedFundings' });
  if (!result) return [];
  return result.map(([coin, venueArr]) => ({
    coin,
    venues: venueArr
      .filter(([, info]) => info?.fundingRate !== undefined && info?.fundingRate !== null)
      .map(([venue, info]) => ({
        venue,
        fundingRate: num(info.fundingRate),
        nextFundingTime: info.nextFundingTime,
      })),
  }));
}

// ── recentTrades (Live-Tape pro Coin) ────────────────────────────────────────

export interface RecentTrade {
  coin: string;
  /** 'A' = Ask (Sell-Aggression), 'B' = Bid (Buy-Aggression). */
  side: 'A' | 'B';
  price: number;
  size: number;
  /** USD-Volumen = price × size. */
  usd: number;
  time: number;
  hash: string;
  users?: string[];
}

interface RecentTradeRaw {
  coin?: string;
  px: string;
  sz: string;
  side: 'A' | 'B';
  time: number;
  hash: string;
  users?: string[];
}

export async function fetchRecentTrades(coin: string, limit = 30): Promise<RecentTrade[]> {
  const result = await postInfo<RecentTradeRaw[]>({ type: 'recentTrades', coin });
  if (!result) return [];
  const trades = result.map((t) => {
    const price = num(t.px);
    const size = num(t.sz);
    return {
      coin: t.coin ?? coin,
      side: t.side,
      price,
      size,
      usd: price * size,
      time: t.time,
      hash: t.hash,
      users: t.users,
    } satisfies RecentTrade;
  });
  // HL liefert in chronologischer Reihenfolge — neueste am Ende. Wir wollen
  // neueste oben, also reverse + slice.
  return trades.reverse().slice(0, limit);
}

// ── l2Book (Order Book Snapshot) ─────────────────────────────────────────────

export interface L2Level {
  price: number;
  size: number;
  /** Anzahl Orders auf diesem Level. */
  n: number;
}

export interface L2Snapshot {
  coin: string;
  /** Server-Zeit. */
  time: number;
  /** Bids absteigend (höchste zuerst). */
  bids: L2Level[];
  /** Asks aufsteigend (niedrigste zuerst). */
  asks: L2Level[];
}

interface L2BookRaw {
  coin: string;
  time: number;
  /** [bids, asks] — jeweils Array von {px, sz, n}. */
  levels: [Array<{ px: string; sz: string; n: number }>, Array<{ px: string; sz: string; n: number }>];
}

export async function fetchL2Book(coin: string): Promise<L2Snapshot | null> {
  const result = await postInfo<L2BookRaw>({ type: 'l2Book', coin });
  if (!result || !result.levels) return null;
  const [bidsRaw, asksRaw] = result.levels;
  const mapLevel = (lvl: { px: string; sz: string; n: number }): L2Level => ({
    price: num(lvl.px),
    size: num(lvl.sz),
    n: typeof lvl.n === 'number' ? lvl.n : 0,
  });
  return {
    coin: result.coin,
    time: result.time,
    bids: bidsRaw.map(mapLevel),
    asks: asksRaw.map(mapLevel),
  };
}

// ── fundingHistory (Plot für die letzten N Stunden) ──────────────────────────

export interface FundingHistoryEntry {
  /** Unix-ms-Timestamp. */
  time: number;
  /** Decimal. */
  fundingRate: number;
  /** Decimal. */
  premium: number;
}

interface FundingHistoryRaw {
  coin: string;
  time: number;
  fundingRate: string;
  premium: string;
}

/**
 * @param coin Coin-Name
 * @param hoursBack default 48 (so wie das Mockup zeigt).
 */
export async function fetchFundingHistory(coin: string, hoursBack = 48): Promise<FundingHistoryEntry[]> {
  const startTime = Date.now() - hoursBack * 60 * 60 * 1000;
  const result = await postInfo<FundingHistoryRaw[]>({
    type: 'fundingHistory',
    coin,
    startTime,
  });
  if (!result) return [];
  return result.map((r) => ({
    time: r.time,
    fundingRate: num(r.fundingRate),
    premium: num(r.premium),
  }));
}

// ── Leaderboard (stats-data.hyperliquid.xyz) ─────────────────────────────────

const HL_STATS_URL = 'https://stats-data.hyperliquid.xyz/Mainnet/leaderboard';

export type LeaderboardWindow = 'day' | 'week' | 'month' | 'allTime';

export interface LeaderboardWindowPerf {
  pnl: number;
  roi: number;
  vlm: number;
}

export interface LeaderboardRow {
  ethAddress: string;
  /** Optionaler Custom-Name (z.B. "James Wynn"). */
  displayName?: string;
  /** Account-Value in USD. */
  accountValue: number;
  /** Perf-Map pro Window. */
  perf: Record<LeaderboardWindow, LeaderboardWindowPerf>;
}

interface LeaderboardRowRaw {
  ethAddress: string;
  displayName?: string | null;
  accountValue?: string | number | null;
  /** Tuples [windowKey, {pnl, roi, vlm}]. */
  windowPerformances?: Array<[LeaderboardWindow, { pnl?: string | null; roi?: string | null; vlm?: string | null }]>;
}

const EMPTY_PERF: LeaderboardWindowPerf = { pnl: 0, roi: 0, vlm: 0 };

export async function fetchLeaderboard(timeoutMs = 12_000): Promise<LeaderboardRow[]> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(HL_STATS_URL, { signal: ctrl.signal });
    if (!res.ok) return [];
    const data = (await res.json()) as { leaderboardRows?: LeaderboardRowRaw[] };
    const rows = data.leaderboardRows ?? [];
    return rows.map((r) => {
      const perfTuples = r.windowPerformances ?? [];
      const perf: Record<LeaderboardWindow, LeaderboardWindowPerf> = {
        day: EMPTY_PERF,
        week: EMPTY_PERF,
        month: EMPTY_PERF,
        allTime: EMPTY_PERF,
      };
      for (const [win, p] of perfTuples) {
        if (win === 'day' || win === 'week' || win === 'month' || win === 'allTime') {
          perf[win] = {
            pnl: num(p?.pnl),
            roi: num(p?.roi),
            vlm: num(p?.vlm),
          };
        }
      }
      return {
        ethAddress: r.ethAddress,
        displayName: r.displayName ?? undefined,
        accountValue: num(r.accountValue),
        perf,
      } satisfies LeaderboardRow;
    });
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') return [];
    console.warn('[hl-info] fetchLeaderboard failed:', e instanceof Error ? e.message : e);
    return [];
  } finally {
    clearTimeout(timer);
  }
}

/**
 * EVE-Score (V1-Port): log10(av+1) × log10(vol+1) × (roi+1).
 * Belohnt Wallets die alle 3 Dimensionen haben: Größe, Aktivität, Profitabilität.
 */
export function eveScore(row: LeaderboardRow, window: LeaderboardWindow): number {
  const p = row.perf[window] ?? EMPTY_PERF;
  const av = row.accountValue;
  const vol = p.vlm;
  const roi = p.roi;
  if (av <= 0 || vol <= 0) return 0;
  return Math.log10(av + 1) * Math.log10(vol + 1) * (roi + 1);
}

// ── ClearinghouseState-Batch-Scan (für Positions-Tab) ────────────────────────

export interface ClearinghouseAssetPosition {
  type?: string;
  position: {
    coin: string;
    szi: string;
    entryPx?: string;
    positionValue?: string;
    unrealizedPnl?: string;
    leverage?: { type?: string; value?: number };
    [key: string]: unknown;
  };
}

export interface ClearinghouseStateRaw {
  marginSummary?: { accountValue?: string; totalMarginUsed?: string; totalNtlPos?: string };
  withdrawable?: string;
  assetPositions?: ClearinghouseAssetPosition[];
}

/**
 * Lädt clearinghouseState für eine Wallet. Wrapper um den POST /info-Endpoint.
 */
export async function fetchClearinghouseStateRaw(
  user: string,
  timeoutMs = 5000,
): Promise<ClearinghouseStateRaw | null> {
  return postInfo<ClearinghouseStateRaw>({ type: 'clearinghouseState', user }, timeoutMs);
}

/**
 * Skannt eine Liste von Adressen parallel in Batches.
 *
 * @param addresses Adressen die skannt werden
 * @param batchSize Wie viele parallel pro Welle (default 8 — HL rate-limit-friendly)
 * @param onProgress Callback `(scanned, total) => void` für UI-Anzeige
 */
export async function scanClearinghouseStates(
  addresses: ReadonlyArray<string>,
  batchSize = 8,
  onProgress?: (scanned: number, total: number) => void,
): Promise<Map<string, ClearinghouseStateRaw>> {
  const results = new Map<string, ClearinghouseStateRaw>();
  for (let i = 0; i < addresses.length; i += batchSize) {
    const batch = addresses.slice(i, i + batchSize);
    const settled = await Promise.allSettled(
      batch.map((addr) => fetchClearinghouseStateRaw(addr)),
    );
    settled.forEach((res, j) => {
      const addr = batch[j];
      if (!addr) return;
      if (res.status === 'fulfilled' && res.value) {
        results.set(addr, res.value);
      }
    });
    onProgress?.(Math.min(i + batchSize, addresses.length), addresses.length);
  }
  return results;
}
